import { useQuery } from "@apollo/client";
import { useLens } from "@hookform/lenses";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Button,
  CircularProgress,
  MenuItem,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash";
import { ReactElement } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { z } from "zod";

import {
  AudienceTargeting as AudienceTargetingGqlType,
  CalculateCampaignContactCountMutation,
  CalculateCampaignContactCountMutationVariables,
  CampaignContactCountJob,
  ImportCampaignContactsFromAudienceMutation,
  ImportCampaignContactsFromAudienceMutationVariables,
  ListAudiencesForSubaccountQuery,
  ListAudiencesForSubaccountQueryVariables,
  TrackedJobStatus,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  convertTargetingFormToGraphQL,
  DEFAULT_TARGET,
  TargetGroups,
} from "~frontend/components/audienceFilteringForm";
import {
  convertGqlAudienceTargetingToFormValues,
  TargetingFormSchema,
} from "~frontend/components/audienceFilteringForm/schema";
import { RhfSelect } from "~frontend/components/forms/react-hook-form-components";
import { LIST_AUDIENCES_FOR_SUBACCOUNT } from "~frontend/graphql/audience";
import {
  AUDIENCE_CAMPAIGN_CONTACT_IMPORT,
  CALCULATE_CAMPAIGN_CONTACT_COUNT_JOB,
  GET_CAMPAIGN,
} from "~frontend/graphql/campaigns";
import {
  SubaccountAdminConfigContextValue,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import theme from "~frontend/theme";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";

// Extend TargetingForm to include audienceId
export const AudienceTargetingFormSchema = TargetingFormSchema.extend({
  audienceId: z.string().min(1, "Please select an audience"),
});

export type AudienceTargetingForm = z.infer<typeof AudienceTargetingFormSchema>;

function ContactCountJobResult({
  job,
}: {
  job: Pick<CampaignContactCountJob, "status" | "result" | "errorMessage">;
}): ReactElement {
  if (
    [
      TrackedJobStatus.Running,
      TrackedJobStatus.Queued,
      TrackedJobStatus.Created,
    ].includes(job.status)
  ) {
    return (
      <Typography
        variant="body2"
        sx={{ marginBottom: theme.spacing(2.5) }}
        component={"div"}
      >
        <FlexBox gap={1} alignItems="center">
          <div>Calculating...</div>
          <CircularProgress
            color="inherit"
            size={12}
            sx={{ marginTop: "-1px" }}
          />
        </FlexBox>
      </Typography>
    );
  }

  if (job.result) {
    return (
      <Typography
        variant="body2"
        sx={{ marginBottom: theme.spacing(2.5) }}
        component={"div"}
      >
        <FlexBox gap={1}>
          <strong>
            {plural(job.result.contactCount, "contact", "contacts")} in this
            campaign
          </strong>
          <span>
            Calculated {new Date(job.result.calculatedAt).toLocaleString()}
          </span>
        </FlexBox>
      </Typography>
    );
  }

  if (job.errorMessage) {
    return (
      <Alert severity="error" sx={{ marginBottom: theme.spacing(2.5) }}>
        {job.errorMessage}
      </Alert>
    );
  }

  return <></>;
}

export default function AudienceTargeting({
  campaign,
  previousJobFilters,
  previousAudienceId,
  readOnly,
}: {
  campaign: CampaignForEditor;
  previousJobFilters: AudienceTargetingGqlType | null;
  previousAudienceId: string | null;
  readOnly?: boolean;
}): ReactElement {
  const subaccountAdminConfig: SubaccountAdminConfigContextValue =
    useSubaccountAdminConfig()!;

  const timezone =
    campaign.textingHoursTimezone || subaccountAdminConfig.textingHoursTimezone;

  const { data: audiencesData, loading: audiencesLoading } = useQuery<
    ListAudiencesForSubaccountQuery,
    ListAudiencesForSubaccountQueryVariables
  >(LIST_AUDIENCES_FOR_SUBACCOUNT, {
    variables: { input: { subaccountId: subaccountAdminConfig.id } },
  });

  const availableAudiences =
    audiencesData?.listAudiencesForSubaccount?.filter(
      a => a.type !== "SUBSCRIBERS" && !a.archivedAt
    ) || [];

  const [startImport] = useMutation<
    ImportCampaignContactsFromAudienceMutation,
    ImportCampaignContactsFromAudienceMutationVariables
  >(AUDIENCE_CAMPAIGN_CONTACT_IMPORT, {
    onErrorSnackbarMessage: "Failed to start audience import",
  });

  const [calculateCampaignContactCount] = useMutation<
    CalculateCampaignContactCountMutation,
    CalculateCampaignContactCountMutationVariables
  >(CALCULATE_CAMPAIGN_CONTACT_COUNT_JOB, {
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
    onErrorSnackbarMessage: "Failed to calculate contact count",
    refetchQueries: [GET_CAMPAIGN],
    awaitRefetchQueries: true,
  });

  const defaultTargetGroup = previousJobFilters
    ? convertGqlAudienceTargetingToFormValues(previousJobFilters)
    : { targetGroups: [] };

  const defaultValues: AudienceTargetingForm = {
    ...defaultTargetGroup,
    audienceId: previousAudienceId || availableAudiences[0]?.id || "",
  };

  const formMethods = useForm<AudienceTargetingForm>({
    resolver: zodResolver(AudienceTargetingFormSchema),
    mode: "all",
    reValidateMode: "onBlur",
    values: defaultValues,
  });

  const { control, handleSubmit, formState, watch } = formMethods;
  const selectedAudienceId = watch("audienceId");

  async function triggerImport(data: AudienceTargetingForm) {
    const { audienceId, ...filters } = data;
    return startImport({
      variables: {
        input: {
          campaignId: campaign.id,
          audienceId,
          filters: convertTargetingFormToGraphQL(filters),
        },
      },
    });
  }

  async function triggerContactCount(data: AudienceTargetingForm) {
    const { audienceId, ...filters } = data;
    return calculateCampaignContactCount({
      variables: {
        input: {
          campaignId: campaign.id,
          audienceId,
          filters: convertTargetingFormToGraphQL(filters),
        },
      },
    });
  }

  const targetGroupsLens = useLens({ control }).focus("targetGroups");
  const targetGroupsFieldArray = useFieldArray(targetGroupsLens.interop());
  const targetGroups = useWatch(targetGroupsLens.interop());

  if (audiencesLoading) {
    return (
      <FlexBox justifyContent="center" alignItems="center">
        <CircularProgress />
      </FlexBox>
    );
  }

  if (availableAudiences.length === 0) {
    return (
      <Alert severity="info">
        No audiences available. Please create an audience first.
      </Alert>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <form name="audienceFilterForm">
        <RhfSelect
          name="audienceId"
          label="Audience"
          disabled={readOnly}
          fullWidth={true}
          sx={{ marginBottom: theme.spacing(3) }}
        >
          {availableAudiences.map(audience => (
            <MenuItem key={audience.id} value={audience.id}>
              {audience.name}
            </MenuItem>
          ))}
        </RhfSelect>

        {targetGroups.length ? (
          <>
            <Typography variant="body1" sx={{ marginBottom: theme.spacing(2) }}>
              Import contacts who meet the criteria for one or more of the
              following filter groups:
            </Typography>

            <TargetGroups
              lens={targetGroupsLens}
              fieldArray={targetGroupsFieldArray}
              audienceId={selectedAudienceId}
              timezone={timezone}
              savedSearchesEnabled={true}
              objectLabel={"contacts"}
            />

            {campaign.contactCountJob && (
              <ContactCountJobResult job={campaign.contactCountJob} />
            )}

            <FlexBox justifyContent="space-between">
              <Button
                color="tertiary"
                variant="contained"
                sx={{ fontSize: theme.spacing(1.5) }}
                onClick={() => targetGroupsFieldArray.append(DEFAULT_TARGET)}
              >
                + Add Filter Group
              </Button>
              <FlexBox direction="row" gap={1}>
                <Button
                  color="tertiary"
                  variant="contained"
                  onClick={handleSubmit(triggerContactCount)}
                  sx={{ fontSize: theme.spacing(1.5) }}
                  disabled={
                    campaign.contactCountJob?.status ===
                      TrackedJobStatus.Running ||
                    !formState.isValid ||
                    !formState.isDirty
                  }
                >
                  {campaign.contactCountJob
                    ? "Refresh Contact Total"
                    : "Preview Contact Total"}
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleSubmit(triggerImport)}
                  disabled={
                    readOnly ||
                    !isEmpty(formState.errors) ||
                    !formState.isDirty ||
                    !formState.isValid
                  }
                  sx={{ fontSize: theme.spacing(1.5) }}
                  data-testid="import-contacts-button"
                >
                  Import{" "}
                  {targetGroupsFieldArray.fields.length > 0 ? "Filtered " : ""}
                  Contacts
                </Button>
              </FlexBox>
            </FlexBox>
          </>
        ) : (
          <FlexBox justifyContent="flex-end" gap={1}>
            <Button
              color="tertiary"
              variant="contained"
              sx={{ fontSize: theme.spacing(1.5) }}
              onClick={() => targetGroupsFieldArray.append(DEFAULT_TARGET)}
            >
              Filter Contacts
            </Button>

            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmit(triggerImport)}
              disabled={readOnly}
              sx={{ fontSize: theme.spacing(1.5) }}
              data-testid="import-contacts-button"
            >
              Import All Contacts in this Audience
            </Button>
          </FlexBox>
        )}
      </form>
    </FormProvider>
  );
}
